const WALL_TIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

export function validTaskTimeZone(zone: string): boolean {
  try { new Intl.DateTimeFormat("en-US", { timeZone: zone }); return true; }
  catch { return false; }
}

export function validWallTime(value: string): boolean {
  const parts = WALL_TIME.exec(value);
  if (!parts) return false;
  const [, year, month, day, hour, minute] = parts.map(Number);
  const date = new Date(Date.UTC(year, month - 1, day, hour, minute));
  return date.getUTCFullYear() === year && date.getUTCMonth() + 1 === month && date.getUTCDate() === day && date.getUTCHours() === hour && date.getUTCMinutes() === minute;
}

function zonedParts(instant: number, zone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(instant);
  const value = (type: Intl.DateTimeFormatPartTypes) => parts.find(part => part.type === type)!.value;
  return `${value("year")}-${value("month")}-${value("day")}T${value("hour")}:${value("minute")}`;
}

export function wallTimeInZone(instant: string, zone: string): string {
  if (!validTaskTimeZone(zone)) throw new Error("Choose a valid task timezone");
  return zonedParts(new Date(instant).getTime(), zone);
}

export function resolveWallTime(wall: string, zone: string): string {
  if (!validWallTime(wall) || !validTaskTimeZone(zone)) throw new Error("Choose a valid local time and timezone");
  const [year, month, day, hour, minute] = WALL_TIME.exec(wall)!.slice(1).map(Number);
  const target = Date.UTC(year, month - 1, day, hour, minute);
  const candidates = new Set<number>();
  for (const sample of [target - 36 * 3_600_000, target, target + 36 * 3_600_000]) {
    const local = zonedParts(sample, zone);
    const [y, m, d, h, min] = WALL_TIME.exec(local)!.slice(1).map(Number);
    const offset = Date.UTC(y, m - 1, d, h, min) - sample;
    candidates.add(Math.round((target - offset) / 60_000) * 60_000);
  }
  const exact = [...candidates].filter(candidate => zonedParts(candidate, zone) === wall).sort((a, b) => a - b);
  if (exact.length) return new Date(exact[0]).toISOString();
  // A spring-forward clock time does not exist; shift to the first valid later wall time.
  const later = [...candidates].filter(candidate => zonedParts(candidate, zone) > wall).sort((a, b) => a - b);
  if (later.length) return new Date(later[0]).toISOString();
  throw new Error("This local time cannot be resolved in the selected timezone");
}

export function shiftWallTime(wall: string | null | undefined, days: number): string | null {
  if (!wall) return null;
  if (!validWallTime(wall)) throw new Error("Invalid local task time");
  const date = new Date(`${wall.slice(0, 10)}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return `${date.toISOString().slice(0, 10)}${wall.slice(10)}`;
}

export function taskTimeMismatch(task: { scheduleMode?: "fixed" | "floating"; scheduleTimeZone?: string | null; wallStartAt?: string | null; wallEndAt?: string | null; startAt?: string | null; endAt?: string | null }): boolean {
  if (task.scheduleMode !== "floating") return false;
  if (!task.scheduleTimeZone) return true;
  try {
    return [[task.wallStartAt, task.startAt], [task.wallEndAt, task.endAt]].some(([wall, instant]) => {
      if (!!wall !== !!instant) return true;
      return !!wall && !!instant && resolveWallTime(wall, task.scheduleTimeZone!) !== new Date(instant).toISOString();
    });
  } catch { return true; }
}
